import * as React from 'react'
import { observer } from 'mobx-react'
import { QuestionEditorComponentProps } from 'jobs/questions/props'
import BoolQuestionViewModel from '../../BoolQuestionViewModel'
import { ToggleGroup, ToggleItem } from 'components/ToggleGroup'
import { Help } from 'components/inputs'
import { last } from 'lodash'
import {
  ItemSource,
  useCustomSelectStateFromItemSource,
  CustomSelect,
} from 'components/inputs/CustomSelect'

interface Props extends QuestionEditorComponentProps<BoolQuestionViewModel> {}

export default observer(function BoolEditorInput({
  vm,
  disabled,
  onEnterDown,
  focus$,
  onFocus,
  isNotValid,
}: Props) {
  const keyDown = React.useCallback(
    (e: React.KeyboardEvent) => e.key === 'Enter' && onEnterDown?.(e),
    [onEnterDown]
  )
  return (
    <>
      <div>
        <ToggleGroup stretch focus$={focus$} onFocus={onFocus}>
          <ToggleItem
            toggled={vm.answer === true}
            onChange={() => vm.change(true)}
            disabled={disabled}
            onKeyDown={keyDown}
            isNotValid={isNotValid}
            dataTest={`bool-answer-yes-${vm.id}`}
          >
            Yes
          </ToggleItem>
          <ToggleItem
            toggled={vm.answer === false}
            onChange={() => vm.change(false)}
            disabled={disabled}
            onKeyDown={keyDown}
            isNotValid={isNotValid}
            dataTest={`bool-answer-no-${vm.id}`}
          >
            No
          </ToggleItem>
        </ToggleGroup>
      </div>
      <Help
        error={vm.settings?.showErrorMessages ? last(vm.validationErrors) : ''}
      />
    </>
  )
})

export const BoolSelectEditorInput = observer(function AdvancedSelect({
  vm,
  disabled,
  onEnterDown,
  focus$,
  onFocus,
}: Props) {
  const itemSource = React.useMemo(() => {
    return ItemSource.static<string>(['Yes', 'No'], (item, text) =>
      item.toLowerCase().includes(text.toLowerCase())
    )
  }, [])
  const itemToString = React.useCallback((itemName: string) => itemName, [])
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })

  return (
    <CustomSelect
      {...selectProps}
      focus$={focus$}
      onFocus={onFocus}
      disabled={disabled}
      onEnterDown={onEnterDown}
      itemToString={itemToString}
      multi={false}
      value={vm.isAnswered ? vm.toTextAnswer() : undefined}
      onChange={(value) => vm.change(value === 'Yes')}
      placeholder={'Select one'}
      data-test={`bool-answer-${vm.id}`}
    />
  )
})
